/*
  TinyCircuits — typewriter headings.

  Types the retro VT323 headings out with a blinking phosphor caret when they scroll
  into view. Ported from the Claude Design handoff ([data-tw] + IntersectionObserver).

  Targets every `.tc-h` heading plus anything explicitly marked `[data-tw]`.
  Opt a heading out with `data-tw="off"`.

  Each heading gets two layers: a hidden "ghost" holding the full text (which keeps the
  real height and line wrapping, so nothing on the page moves while typing) and a live
  layer stacked on top that gets rebuilt character by character.
*/
(function () {
  var SELECTOR = '.tc-h:not([data-tw="off"]), [data-tw]:not([data-tw="off"])';
  var script = document.currentScript;
  var replay = !script || script.getAttribute('data-replay') !== 'false';

  /* Honour reduced-motion: leave the headings exactly as rendered. */
  if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  if (!('IntersectionObserver' in window)) return;

  var observer = null;
  var timers = [];
  var delivered = false;

  function caret() {
    var c = document.createElement('span');
    c.className = 'tc-tw__caret';
    c.setAttribute('aria-hidden', 'true');
    c.textContent = '_';
    return c;
  }

  /* Liquid indentation leaves newlines and runs of spaces inside the heading. Left alone
     they'd be counted (and "typed") as dozens of invisible characters. */
  function normalize(node) {
    var texts = [];
    (function walk(n) {
      for (var i = 0; i < n.childNodes.length; i++) {
        var c = n.childNodes[i];
        if (c.nodeType === 3) { c.nodeValue = c.nodeValue.replace(/\s+/g, ' '); texts.push(c); }
        else if (c.nodeType === 1) walk(c);
      }
    })(node);
    if (texts.length) {
      texts[0].nodeValue = texts[0].nodeValue.replace(/^ /, '');
      texts[texts.length - 1].nodeValue = texts[texts.length - 1].nodeValue.replace(/ $/, '');
    }
    return node;
  }

  /* Clone the heading's first `limit` characters, preserving nested markup
     (the accent <span>, <br>, the pixel heart <svg>, …). */
  function truncate(source, limit) {
    var frag = document.createDocumentFragment();
    var used = 0;

    (function walk(from, to) {
      var kids = from.childNodes;
      for (var i = 0; i < kids.length; i++) {
        if (used >= limit) return;
        var child = kids[i];
        if (child.nodeType === 3) {
          var text = child.nodeValue;
          var take = Math.min(text.length, limit - used);
          to.appendChild(document.createTextNode(text.slice(0, take)));
          used += take;
        } else if (child.nodeType === 1) {
          var clone = child.cloneNode(false);
          to.appendChild(clone);
          /* <br>, <svg> and friends carry no text, so they cost nothing and ride
             along with the character before them. */
          walk(child, clone);
        }
      }
    })(source, frag);

    return frag;
  }

  function render(el, n, withCaret) {
    el._twLive.innerHTML = '';
    el._twLive.appendChild(truncate(el._twFull, n));
    if (withCaret !== false) el._twLive.appendChild(caret());
  }

  /* Fail-safe: put a heading back to its full text, no caret. */
  function reveal(el) {
    if (!el._twInit) return;
    el._twState = 'done';
    render(el, el._twLen, false);
  }

  function start(el) {
    if (el._twState === 'typing' || el._twState === 'done') return;
    el._twState = 'typing';
    var n = 0;
    var speed = Math.max(28, Math.min(70, 900 / Math.max(1, el._twLen)));
    var timer = setInterval(function () {
      n++;
      render(el, n);
      if (n >= el._twLen) {
        clearInterval(timer);
        el._twState = 'done';
        if (!replay && observer) observer.unobserve(el);
      }
    }, speed);
    timers.push(timer);
  }

  function setup(el) {
    if (el._twInit) return;

    var full = normalize(el.cloneNode(true));
    var text = (full.textContent || '').trim();
    if (!text) return;

    el._twInit = true;
    el._twFull = full;
    el._twLen = (full.textContent || '').length;
    el._twState = 'idle';

    /* Ghost keeps the finished size; live layer sits on top of it. */
    var ghost = document.createElement('span');
    ghost.className = 'tc-tw__ghost';
    ghost.setAttribute('aria-hidden', 'true');
    while (full.firstChild) ghost.appendChild(full.firstChild);
    el._twFull = ghost.cloneNode(true);
    /* The live layer always carries a caret, so the ghost needs one too — otherwise the
       live text is a character wider than the box and wraps an extra line out of it. */
    ghost.appendChild(caret());

    var live = document.createElement('span');
    live.className = 'tc-tw__live';
    el._twLive = live;

    el.innerHTML = '';
    el.style.position = 'relative';
    el.appendChild(ghost);
    el.appendChild(live);

    /* Keep the whole heading available to assistive tech while it's partially typed. */
    if (!el.getAttribute('aria-label')) el.setAttribute('aria-label', text);

    render(el, 0);
    if (observer) observer.observe(el);
  }

  function init() {
    if (!observer) {
      observer = new IntersectionObserver(function (entries) {
        delivered = true;
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            start(entry.target);
          } else if (replay) {
            entry.target._twState = 'idle';
            render(entry.target, 0);
          }
        });
      }, { threshold: 0.6 });

      /* A healthy browser reports every observed element almost immediately, in view or
         not. If nothing ever arrives the headings would sit there blank — so show them. */
      setTimeout(function () {
        if (delivered) return;
        Array.prototype.forEach.call(document.querySelectorAll(SELECTOR), reveal);
        observer.disconnect();
        observer = null;
      }, 1500);
    }
    Array.prototype.forEach.call(document.querySelectorAll(SELECTOR), setup);
  }

  window.tcInitTypewriters = init;

  /* Wait for VT323 so the ghost is sized against the real font. */
  var ready = document.fonts && document.fonts.ready ? document.fonts.ready : Promise.resolve();
  ready.then(init).catch(init);

  /* Theme editor re-renders sections in place. */
  document.addEventListener('shopify:section:load', function () {
    if (document.fonts && document.fonts.ready) { document.fonts.ready.then(init); } else { init(); }
  });

  window.addEventListener('pagehide', function () {
    timers.forEach(clearInterval);
    if (observer) observer.disconnect();
  });
})();
